// Reference:
// * https://github.com/epicweb-dev/epic-stack/blob/90ef60d4b2e762a1888fc72abd71f40a5142f978/app/routes/resources+/theme-switch.tsx

import { getFormProps, useForm } from "@conform-to/react";
import { Monitor, MonitorCheck, MonitorOff, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useFetcher } from "react-router";
import { ServerOnly } from "remix-utils/server-only";

import { Button } from "@/ui/button";
import { Separator } from "@/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/tooltip";
import { useRequestInfo } from "@/utils/request-info";
import { cn } from "@/utils/tailwind";

import { useOptimisticThemeMode, useTheme } from "./theme";

interface ThemeControlProps {
  className?: string;
}

export function ThemeControl({ className }: ThemeControlProps) {
  const [showIntermediateMonitorIcon, setShowIntermediateMonitorIcon] =
    useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const requestInfo = useRequestInfo();
  const currentTheme = useTheme();
  const optimisticMode = useOptimisticThemeMode();
  const fetcher = useFetcher();
  const [form] = useForm({
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
                                @typescript-eslint/no-unsafe-member-access
    */
    lastResult: fetcher.data?.result,
  });

  const isDark = currentTheme === "dark";
  const isSystem =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (optimisticMode ?? requestInfo.userPreference.theme ?? "system") ===
    "system";

  useEffect(() => {
    if (showIntermediateMonitorIcon) {
      const timer = setTimeout(() => {
        setShowIntermediateMonitorIcon(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [showIntermediateMonitorIcon]);

  return (
    <fetcher.Form
      method="POST"
      {...getFormProps(form)}
      action="/theme"
      aria-label="Theme control"
      className="flex gap-3"
    >
      <ServerOnly>
        {() => (
          /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,
                                      @typescript-eslint/no-unsafe-member-access
          */
          <input name="redirectTo" type="hidden" value={requestInfo.path} />
        )}
      </ServerOnly>

      <div className={cn("flex items-center", className)}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={`Switch to ${isDark ? "light" : "dark"} theme`}
                name="theme"
                size="icon"
                type="submit"
                value={isDark ? "light" : "dark"}
                variant="ghost"
                className={
                  isSystem
                    ? "text-muted-foreground hover:text-foreground"
                    : "text-foreground"
                }
                onClick={() => isSystem && setShowIntermediateMonitorIcon(true)}
              >
                <span className="grid size-4">
                  <Sun
                    className={cn(
                      "size-4 transition-all duration-300 [grid-area:1/1] motion-reduce:transition-none",
                      isDark
                        ? "scale-0 rotate-90 opacity-0"
                        : "scale-100 rotate-0 opacity-100",
                    )}
                  />
                  <Moon
                    className={cn(
                      "size-4 transition-all duration-300 [grid-area:1/1] motion-reduce:transition-none",
                      isDark
                        ? "scale-100 rotate-0 opacity-100"
                        : "scale-0 -rotate-90 opacity-0",
                    )}
                  />
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={5}>
              Toggle theme
            </TooltipContent>
          </Tooltip>

          <Separator
            className="bg-muted-foreground data-[orientation=vertical]:h-3.5"
            orientation="vertical"
          />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Use system theme"
                aria-pressed={isSystem}
                name="theme"
                size="icon"
                type="submit"
                value="system"
                variant="ghost"
                className={
                  isSystem
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }
                onClick={() => setShowIntermediateMonitorIcon(true)}
              >
                {isSystem ? (
                  showIntermediateMonitorIcon ? (
                    <Monitor className="size-4" />
                  ) : (
                    <MonitorCheck className="size-4" />
                  )
                ) : showIntermediateMonitorIcon ? (
                  <Monitor className="size-4" />
                ) : (
                  <MonitorOff className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={5}>
              Use system theme
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </fetcher.Form>
  );
}
